import React from "react";
import { COMPANY_CONFIG } from "../data/companyConfig";
import { Lock, Zap, Headphones, CheckCircle2, ArrowRight } from "lucide-react";

export const WhyChooseUs = ({ onContactClick }) => {
    const features = [
        {
            icon: Zap,
            title: "Instant Bill Settlements",
            description:
                "Direct connection to central payment rails guarantees instant bill verification, payment execution, and immediate receipt generation.",
            color: "text-blue-600 bg-blue-50 border-blue-200",
        },
        {
            icon: Lock,
            title: "256-Bit SSL Encryption",
            description:
                "All customer data, consumer numbers, and financial transactions are shielded by rigorous 256-bit bank-grade SSL cryptographic standards.",
            color: "text-emerald-600 bg-emerald-50 border-emerald-200",
        },
        {
            icon: Headphones,
            title: "24/7 Dedicated Support",
            description: `Our Hyderabad customer helpdesk is available at ${COMPANY_CONFIG.phone} and ${COMPANY_CONFIG.email} for instant assistance.`,
            color: "text-blue-700 bg-blue-50 border-blue-200",
        },
        {
            icon: CheckCircle2,
            title: "High Transaction Success",
            description:
                "Engineered with automated failover switches across multiple payment nodes to ensure consistent, uninterrupted payment success.",
            color: "text-emerald-600 bg-emerald-50 border-emerald-200",
        },
    ];

    return (
        <section
            id="why-choose-us"
            className="py-16 sm:py-24 bg-white text-slate-900 border-b border-slate-200"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header matching PaySwitch */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold uppercase tracking-wider mb-3">
                        Built on Trust, Speed &amp; Security
                    </div>
                    <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight font-['Outfit',sans-serif]">
                        Why Choose Global Bizness
                    </h2>
                    <p className="mt-4 text-base text-slate-600">
                        Providing reliable, bank-grade payment infrastructure for businesses
                        and individuals nationwide.
                    </p>
                </div>

                {/* 4 Value Grid (Crisp White Cards with Blue and Green Accents) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                    {features.map((feature, idx) => {
                        const Icon = feature.icon;
                        return (
                            <div
                                key={idx}
                                className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs hover:border-blue-400 hover:shadow-md transition-all space-y-3"
                            >
                                <div
                                    className={`w-12 h-12 rounded-xl flex items-center justify-center border ${feature.color}`}
                                >
                                    <Icon className="w-6 h-6" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 font-['Outfit',sans-serif]">
                                    {feature.title}
                                </h3>
                                <p className="text-xs text-slate-600 leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* Call to Action Banner matching PaySwitch (Deep Blue to Rich Green Gradient) */}
                <div className="relative overflow-hidden bg-gradient-to-r from-blue-900 via-blue-800 to-emerald-800 rounded-3xl p-8 sm:p-12 border border-blue-900 shadow-xl">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative max-w-3xl space-y-4">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 text-xs font-bold uppercase tracking-wider">
                            Empowering Financial Freedom
                        </div>
                        <h3 className="text-2xl sm:text-4xl font-black text-white font-['Outfit',sans-serif] tracking-tight">
                            Ready to Transform your Payment Experience?
                        </h3>
                        <p className="text-sm sm:text-base text-blue-100 max-w-xl font-normal leading-relaxed">
                            Join with growing Global Bizness family. Join Today. Experience
                            seamless utility payments, merchant POS tools, and instant digital
                            settlements.
                        </p>

                        <div className="pt-2 flex flex-wrap items-center gap-4">
                            <button
                                onClick={onContactClick}
                                className="px-6 py-3.5 rounded-xl font-bold text-xs sm:text-sm text-slate-950 bg-emerald-400 hover:bg-emerald-300 transition-colors shadow-lg shadow-emerald-500/20 flex items-center gap-2 cursor-pointer"
                            >
                                <span>Join Today</span>
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            <a
                                href={`tel:${COMPANY_CONFIG.phoneClean}`}
                                className="px-6 py-3.5 rounded-xl font-bold text-xs sm:text-sm text-white bg-white/10 hover:bg-white/20 border border-white/20 transition-colors flex items-center gap-2"
                            >
                                <span>Helpline: {COMPANY_CONFIG.phone}</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
